import { useNavigate, useParams } from 'react-router-dom';
import { Box, Grid, Typography } from '@mui/material';
import { routes } from 'Routes';
import { useCompanyRelativePath } from 'ogp/hooks';
import { AuthenticatedElement } from 'ogp/components/common/authenticated-element';
import { CompanyRole, TakeDefined, vatLabel } from '@types';
import { PolicyGroups } from 'shared/utils/policy-controller';
import { RenderUnsettledUI } from 'shared/components';
import { useDialog } from 'ogp/providers/dialog-provider';
import { useArchiveWorkTemplate } from 'ogp/hooks/queryHooks/jobs/use-archive-work-template';
import { useAlert, workTemplateAlerts } from 'shared/components/alerts';
import { invariant } from 'shared/utils/utils';
import { formatTextValue, Header, LwButton, Page, Toolbox } from 'redesign';
import { IconDuplicate, IconEdit, IconTrash } from 'assets/img';
import { useGetWorkTemplateCreateData } from 'ogp/hooks/queryHooks/jobs/use-get-work-template-create-data';
import { formatDuration } from 'shared/utils/formatting';
import { Duration } from 'luxon';
import { useGetDepartmentAsDetail } from 'ogp/components/views/management/departments';
import { usePageProvider } from 'ogp/layouts';
import { useGetWorkTemplate, WorkTemplateDetail } from 'api';
import { useExternalParams, useSiteDetection } from 'shared/hooks';
import { TFunctionNonStrict } from 'i18next';
import { useTranslation } from 'react-i18next';
import { GetBranchOfficeResponse } from '../../../../../../services/branch-office-service.types';
import { isFreelanceWorkTemplate, isTempWorkTemplate } from '../utils';
import { WORK_TEMPLATE_INITIAL_VALUES } from '../../work-templates.types';

type Job<JobFormData> = JobFormData & {
  department: Pick<GetBranchOfficeResponse['branchOffice'], 'name'>;
  jobType: string | undefined;
  isFreelance: boolean;
  isTempwork: boolean;
  hourlyRate?: number;
  mutableUntilHours?: number;
  cla?: string;
};

export const WorkTemplateDetailDataProvider = () => {
  const { id } = useParams<'id'>();
  invariant(id, 'Job ID must be defined');

  const { site } = useSiteDetection();
  const jobQuery = useGetWorkTemplate(site, id, !!id);
  const departmentQuery = useGetDepartmentAsDetail(jobQuery.data?.departmentId ?? undefined);
  const workTemplateCreateDataQuery = useGetWorkTemplateCreateData();

  if (workTemplateCreateDataQuery.status !== 'success') {
    return <RenderUnsettledUI data={workTemplateCreateDataQuery} />;
  }

  if (jobQuery.status !== 'success') {
    return <RenderUnsettledUI data={jobQuery} />;
  }

  const data = {
    ...transformJob(jobQuery.data, departmentQuery.data, workTemplateCreateDataQuery.data),
  };

  return <JobDetail data={data} />;
};

const JobDetail = ({ data }: { data: Job<WorkTemplateDetail> }) => {
  const { t } = useTranslation('ogp-screen-specific');
  const navigate = useNavigate();
  const { id } = useParams<'id'>();
  const [{ filters }] = usePageProvider();
  const generatePath = useCompanyRelativePath();
  const labelGroups = getLabelGroups(data, t);
  const { alertSuccess, alertError } = useAlert();
  const archiveJob = useArchiveWorkTemplate({
    onSuccess: () => {
      alertSuccess(workTemplateAlerts.success.deleted);
      navigate(generatePath(routes.WorkTemplates, undefined, queryParams));
      closeDialog();
    },
    onError: (e) => alertError(e),
    onSettled: () => {
      enableDialog();
    },
  });
  const { openDialog, closeDialog, enableDialog, disableDialog } = useDialog();

  const handleArchiveJob = () => {
    invariant(id, 'Job id is not defined');
    disableDialog();
    archiveJob.mutate(id);
  };

  const jobRequirementsMapped = [
    ...(data.jobRequirements?.skills || []),
    ...(data.jobRequirements?.language || []),
    ...(data.jobRequirements?.apparel || []),
  ];

  const handleDelete = () => {
    openDialog({ title: 'Weet je zeker dat je deze functie wilt verwijderen?' }, handleArchiveJob);
  };
  const [params] = useExternalParams(WORK_TEMPLATE_INITIAL_VALUES);
  const queryParams = {
    ...filters,
    search: params.search || undefined,
  };
  const redirectPath = generatePath(routes.WorkTemplates, undefined, queryParams);
  return (
    <Page
      header={
        <Header
          labelGroups={labelGroups}
          titleText={data.name}
          subheaderText="Functie details"
        ></Header>
      }
      toolbox={
        <Toolbox>
          <LwButton to={redirectPath} color="secondary">
            Terug
          </LwButton>
          <LwButton
            to={generatePath(routes.WorkTemplatesEdit, { id }, queryParams)}
            startIcon={<IconEdit />}
            iconColorMode="fill"
            color="secondary"
          >
            Functie aanpassen
          </LwButton>
          <LwButton
            to={generatePath(routes.WorkTemplatesDuplicate, { id }, queryParams)}
            startIcon={<IconDuplicate />}
            iconColorMode="fill"
            color="secondary"
          >
            Functie dupliceren
          </LwButton>
          <AuthenticatedElement roles={CompanyRole.ADMIN} policyGroup={PolicyGroups.COMPANY}>
            <LwButton
              onClick={handleDelete}
              startIcon={<IconTrash />}
              iconColorMode="stroke"
              color="secondary"
            >
              Verwijderen
            </LwButton>
          </AuthenticatedElement>
        </Toolbox>
      }
    >
      <Box>
        <Grid container spacing={6}>
          <Grid item xs={3}>
            <Typography variant="subtitle2" color="secondary">
              Enkel binnen de flexpool
            </Typography>
            <Typography variant="body1">{data.flexPoolOnly ? 'Ja' : 'Nee'}</Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="subtitle2" color="secondary">
              ORT toegepast
            </Typography>
            <Typography variant="body1">
              Zelfstandige {data.freelanceJob?.ortApplicable ? 'Ja' : 'Nee'} | Uitzendkracht{' '}
              {data.tempWorkJob?.ortApplicable ? 'Ja' : 'Nee'}
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="subtitle2" color="secondary">
              BTW-tarief
            </Typography>
            <Typography variant="body1">{vatLabel(data.VAT)}</Typography>
          </Grid>

          <Grid item xs={3}>
            <Typography variant="subtitle2" color="secondary">
              Locatie
            </Typography>
            <Typography variant="body1">{data.location?.address}</Typography>
          </Grid>

          <Grid item xs={3}>
            <Typography variant="subtitle2" color="secondary">
              Functiegroep
            </Typography>
            <Typography variant="body1">{data.jobType}</Typography>
          </Grid>

          <Grid item xs={3}>
            <Typography variant="subtitle2" color="secondary">
              Contactpersoon
            </Typography>
            <Typography variant="body1">{data.contactName}</Typography>
          </Grid>

          <Grid item xs={3}>
            <Typography variant="subtitle2" color="secondary">
              Telefoon contactpersoon
            </Typography>
            <Typography variant="body1">{data.contactPhoneNumber}</Typography>
          </Grid>

          <Grid item xs={3}>
            <Typography variant="subtitle2" color="secondary">
              Locatie URL
            </Typography>
            <Typography variant="body1">
              <a href={data.locationUrl} target="_blank" rel="noreferrer">
                Link naar locatie
              </a>
            </Typography>
          </Grid>

          <Grid item xs={3}>
            <Typography variant="subtitle2" color="secondary">
              Kostenplaats
            </Typography>
            <Typography variant="body1">{data.costCenter}</Typography>
          </Grid>

          <Grid item xs={3}>
            <Typography variant="subtitle2" color="secondary">
              Korte omschrijving
            </Typography>
            <Typography variant="body1">{data.descriptionShort}</Typography>
          </Grid>

          <Grid item xs={3}>
            <Typography variant="subtitle2" color="secondary">
              Lange omschrijving
            </Typography>
            <Typography variant="body1">{data.descriptionLong}</Typography>
          </Grid>

          <Grid item xs={3}>
            <Typography variant="subtitle2" color="secondary">
              Afspraken en vereisten
            </Typography>
            <Typography variant="body1">{data.descriptionLawRequirements}</Typography>
          </Grid>

          <Grid item xs={3}>
            <Typography variant="subtitle2" color="secondary">
              Vereiste certificaten
            </Typography>
            {data.requiredCertificates?.length ? (
              <CertfificateList certificates={data.requiredCertificates} />
            ) : (
              <Typography variant="body1">Er zijn geen certificaten vereist</Typography>
            )}
          </Grid>
          <Grid item xs={3}>
            <Typography variant="subtitle2" color="secondary">
              Aanvullende certificaten
            </Typography>
            {data.jobCertificates?.length ? (
              <CertfificateList certificates={data.jobCertificates} />
            ) : (
              <Typography variant="body1">Er zijn geen certificaten vereist</Typography>
            )}
          </Grid>
          <Grid item xs={3}>
            <Typography variant="subtitle2" color="secondary">
              Werkvereisten
            </Typography>
            {jobRequirementsMapped.length > 0 ? (
              jobRequirementsMapped.map((jobRequirement) => (
                <Typography key={jobRequirement.id} variant="body1">
                  {jobRequirement.name}
                </Typography>
              ))
            ) : (
              <Typography variant="body1">Geen werkvereisten</Typography>
            )}
          </Grid>
          <Grid item xs={3}>
            <Typography variant="subtitle2" color="secondary">
              Flexpools
            </Typography>
            {data.flexPools.length > 0 ? (
              data.flexPools.map((flexPool) => (
                <Typography key={flexPool.id} variant="body1">
                  {flexPool.name} -{' '}
                  {flexPool.directClaimAllowed
                    ? 'Directe claim toegestaan'
                    : 'Directe claim niet toegestaan'}
                </Typography>
              ))
            ) : (
              <Typography variant="body1">Geen flexpools</Typography>
            )}
          </Grid>
        </Grid>
      </Box>
    </Page>
  );
};

const CertfificateList = ({ certificates }: { certificates: { id: string; name: string }[] }) => {
  return (
    <Grid item xs={8}>
      {certificates.map((certificate) => (
        <Typography key={certificate.id} variant="body1">
          {certificate.name}
        </Typography>
      ))}
    </Grid>
  );
};

const getLabelGroups = (
  data: Job<WorkTemplateDetail>,
  t: TFunctionNonStrict<'ogp-screen-specific', undefined>
) => {
  const result = [
    {
      title: t('work.detail.table_headers.branch_office', { ns: 'ogp-screen-specific' }),
      description: data.department.name,
    },
  ];
  if (data.isFreelance) {
    result.push({
      title: t('work.detail.table_headers.hourly_rate', { ns: 'ogp-screen-specific' }),
      description: formatTextValue(data.hourlyRate, 'currency'),
    });
    result.push({
      title: t('work.detail.table_headers.cancellation_period', { ns: 'ogp-screen-specific' }),
      description: formatDuration(
        Duration.fromMillis((data.mutableUntilHours ?? 0) * 60 * 60 * 1000).toObject()
      ),
    });
    result.push({
      title: t('work.detail.table_headers.break', { ns: 'ogp-screen-specific' }),
      description: formatDuration(Duration.fromMillis(data.breakMinutes * 60 * 1000).toObject()),
    });
  }

  return result;
};

const transformJob = (
  job: WorkTemplateDetail,
  departmentData: TakeDefined<ReturnType<typeof useGetDepartmentAsDetail>['data']> | undefined,
  jobCreateData: TakeDefined<ReturnType<typeof useGetWorkTemplateCreateData>['data']>
): Job<WorkTemplateDetail> => {
  const department = {
    name: departmentData?.name ?? '',
  };

  const jobType = jobCreateData.jobGroups.find((jg) =>
    jg.jobType.find((jt) => jt.id === job.jobTypeId)
  )?.name;

  const common: Partial<Job<WorkTemplateDetail>> = {
    ...job,
    department,
    jobType,
    isFreelance: false,
    isTempwork: false,
  };

  const freelancePart = isFreelanceWorkTemplate(job)
    ? {
        isFreelance: true,
        hourlyRate: job.freelanceJob.hourlyRateCents / 100,
        mutableUntilHours: job.freelanceJob.mutableUntilMinutes / 60,
      }
    : {};

  const tempWorkPart = isTempWorkTemplate(job)
    ? {
        isTempwork: true,
        cla: job.tempWorkJob.cla,
      }
    : {};

  return {
    ...common,
    ...freelancePart,
    ...tempWorkPart,
  } as Job<WorkTemplateDetail>;
};
